// EverSoul Legacy Intelligence Index 2026 — structured data layer.
// Classification values: "published" | "calculated" | "modeled" | "framework"

export const ACCESS_DATE = "June 2026";

export const SOURCES = [
  {
    id: "cerulli-2024",
    org: "Cerulli Associates",
    title: "U.S. High-Net-Worth and Ultra-High-Net-Worth Markets 2024 — Wealth Transfer",
    year: 2024,
    accessed: ACCESS_DATE,
    url: "https://www.cerulli.com/press-releases",
    geography: "United States",
    methodology: "Household-level projection of assets transferring to heirs and charity through 2048.",
    limitations: "US-only; sensitive to asset-return and mortality assumptions.",
    classification: "published",
  },
  {
    id: "ubs-gwr-2024",
    org: "UBS",
    title: "Global Wealth Report 2024",
    year: 2024,
    accessed: ACCESS_DATE,
    url: "https://www.ubs.com/global/en/family-office-uhnw/reports/global-wealth-report-2024.html",
    geography: "Global",
    methodology: "Estimates of intergenerational and intra-generational (spousal) wealth transfer over ~20–25 years.",
    limitations: "Aggregates across jurisdictions with differing data quality.",
    classification: "published",
  },
  {
    id: "knight-frank-2024",
    org: "Knight Frank",
    title: "The Wealth Report 2024",
    year: 2024,
    accessed: ACCESS_DATE,
    url: "https://www.knightfrank.com/wealthreport",
    geography: "Global (HNW)",
    methodology: "Survey and modelling of high-net-worth wealth and generational transfer intentions.",
    limitations: "Survey-based; skewed toward advised HNW households.",
    classification: "published",
  },
  {
    id: "federal-reserve-scf",
    org: "U.S. Federal Reserve",
    title: "Survey of Consumer Finances / Distributional Financial Accounts",
    year: 2023,
    accessed: ACCESS_DATE,
    url: "https://www.federalreserve.gov/econres/scfindex.htm",
    geography: "United States",
    methodology: "Official household balance-sheet and wealth-distribution data.",
    limitations: "Describes stocks of wealth, not forward transfer forecasts.",
    classification: "published",
  },
  {
    id: "nist-ai-rmf",
    org: "U.S. National Institute of Standards and Technology",
    title: "AI Risk Management Framework (AI RMF 1.0)",
    year: 2023,
    accessed: ACCESS_DATE,
    url: "https://www.nist.gov/itl/ai-risk-management-framework",
    geography: "United States / International reference",
    methodology: "Voluntary framework: Govern, Map, Measure, Manage functions for AI risk.",
    limitations: "Framework, not a market forecast; requires interpretation per use case.",
    classification: "framework",
  },
  {
    id: "eversoul-index-2026",
    org: "EverSoul",
    title: "Legacy Intelligence Index — Analytical Model 2026",
    year: 2026,
    accessed: ACCESS_DATE,
    url: "#methodology",
    geography: "Global model",
    methodology: "EverSoul-constructed indices scoring AI capability and institutional governance readiness on a 0–100 scale, with conservative/expected/accelerated scenarios.",
    limitations: "Analytical model, not an externally published market index. Scenarios are illustrative, not predictions.",
    classification: "modeled",
  },
];

export const sourceById = (id) => SOURCES.find((s) => s.id === id);

// ── Convergence: four forces, 2020–2050, by scenario ──────────────────────────
const YEARS = Array.from({ length: 7 }, (_, i) => 2020 + i * 5); // 2020..2050

const lerpSeries = (start, end, curve = 1) =>
  YEARS.map((y, i) => {
    const t = i / (YEARS.length - 1);
    const v = start + (end - start) * Math.pow(t, curve);
    return Math.round(v);
  });

export const CONVERGENCE = {
  years: YEARS,
  scenarios: {
    conservative: {
      aiCapability: lerpSeries(18, 82, 1.35),
      personalAiPersistence: lerpSeries(4, 62, 1.8),
      wealthTransfer: lerpSeries(22, 70, 1),
      governanceImportance: lerpSeries(10, 55, 1.5),
    },
    expected: {
      aiCapability: lerpSeries(20, 92, 1.15),
      personalAiPersistence: lerpSeries(5, 80, 1.6),
      wealthTransfer: lerpSeries(24, 78, 1),
      governanceImportance: lerpSeries(12, 68, 1.4),
    },
    accelerated: {
      aiCapability: lerpSeries(22, 98, 0.95),
      personalAiPersistence: lerpSeries(6, 92, 1.35),
      wealthTransfer: lerpSeries(26, 82, 0.95),
      governanceImportance: lerpSeries(14, 74, 1.3),
    },
  },
  // Point at which lines cross from published/calculated into modeled territory.
  publishedThroughYear: 2025,
  series: [
    { key: "aiCapability", label: "AI capability & adoption", color: "#3A5A7A", classification: "modeled" },
    { key: "personalAiPersistence", label: "Personal AI persistence", color: "#B08D57", classification: "modeled" },
    { key: "wealthTransfer", label: "Intergenerational wealth transfer", color: "#6E7B8B", classification: "calculated" },
    { key: "governanceImportance", label: "Governance & fiduciary importance", color: "#1A1D24", classification: "modeled" },
  ],
};

// ── Wealth transfer forecasts (published, kept separate — not merged) ─────────
export const WEALTH_FORECASTS = [
  {
    id: "wf-cerulli-us",
    sourceId: "cerulli-2024",
    total: "$124 trillion",
    geography: "United States",
    forecastPeriod: "2024–2048",
    note: "Of which an estimated ~$105T passes to heirs and ~$18T to charity.",
    categories: ["global", "us", "hnw", "charitable"],
  },
  {
    id: "wf-ubs-global",
    sourceId: "ubs-gwr-2024",
    total: "$83+ trillion",
    geography: "Global",
    forecastPeriod: "~2024–2049 (20–25 yrs)",
    note: "Includes both intergenerational and horizontal (spousal) transfers.",
    categories: ["global"],
  },
  {
    id: "wf-knightfrank-hnw",
    sourceId: "knight-frank-2024",
    total: "$90+ trillion (HNW cohorts)",
    geography: "Global (HNW)",
    forecastPeriod: "Next ~20 years",
    note: "Focused on high-net-worth and ultra-high-net-worth households.",
    categories: ["global", "hnw"],
  },
  {
    id: "wf-family-business",
    sourceId: "knight-frank-2024",
    total: "Significant share tied to operating businesses",
    geography: "Global",
    forecastPeriod: "Ongoing",
    note: "A large portion of transferring wealth is held in family-owned businesses requiring succession.",
    categories: ["business"],
  },
];

export const WEALTH_TOGGLES = [
  { key: "global", label: "Global estimates" },
  { key: "us", label: "United States" },
  { key: "hnw", label: "High-net-worth households" },
  { key: "business", label: "Family business succession" },
  { key: "charitable", label: "Charitable transfers" },
];

// ── Inheritance expansion stages ──────────────────────────────────────────────
export const INHERITANCE_STAGES = [
  { k: "Physical property", body: "Land, homes, objects — the oldest and most established form of inheritance." },
  { k: "Financial assets", body: "Cash, securities, and instruments governed by mature legal and fiduciary systems." },
  { k: "Businesses", body: "Operating enterprises requiring succession planning and governance continuity." },
  { k: "Intellectual property", body: "Patents, copyrights, and royalties transferred under established IP law." },
  { k: "Digital accounts & archives", body: "Email, cloud storage, and platform accounts — governed unevenly across jurisdictions." },
  { k: "Cryptocurrency & digital assets", body: "Self-custodied and tokenized assets introducing new key-management and access questions." },
  { k: "Voice & likeness", body: "Synthesizable identity attributes now protected inconsistently and increasingly contested." },
  { k: "Persistent personal AI", body: "An interactive representation trained on a person's memory, values, and communication.", detail: ["Voice", "Likeness", "Memories", "Communication style", "Values", "Guidance", "Personal history", "Decision principles", "Interactive family context"] },
  { k: "Governed interactive identity", body: "A consent-governed, fiduciary-supervised personal AI with defined access and retirement — the emerging frontier." },
];

// ── Governance gap (two-line) ─────────────────────────────────────────────────
export const GAP = {
  years: YEARS,
  aiCapability: CONVERGENCE.scenarios.expected.aiCapability,
  governanceReadiness: lerpSeries(8, 46, 1.1),
  aiFactors: [
    "Conversational realism", "Voice synthesis", "Visual likeness", "Persistent memory",
    "Behavioral personalization", "Long-term context", "Autonomous interaction", "Multimodal identity representation",
  ],
  govFactors: [
    "Legal clarity", "Consent standards", "Trust document coverage", "Fiduciary authority",
    "Identity authentication", "Access controls", "Audit infrastructure", "Modification restrictions",
    "Retirement procedures", "Vendor continuity",
  ],
};

// ── Institutional risk matrix ─────────────────────────────────────────────────
export const RISK_ZONES = [
  {
    id: "passive", title: "Passive archive", x: 0, y: 0,
    examples: ["Photographs", "Letters", "Recorded stories", "Family documents"],
  },
  {
    id: "interactive", title: "Interactive family history", x: 1, y: 0,
    examples: ["Searchable memories", "Narrated experiences", "Family history questions", "Cultural & personal context"],
  },
  {
    id: "advisory", title: "Advisory personal AI", x: 0, y: 1,
    examples: ["Life guidance", "Values", "Business philosophy", "Philanthropic principles", "Educational messages", "Leadership guidance"],
  },
  {
    id: "fiduciary", title: "Fiduciary significance", x: 1, y: 1, required: true,
    examples: ["Influence over heirs", "Business succession", "Family governance", "Trust distributions", "Confidential family matters", "Stewardship obligations", "Disputes between beneficiaries", "Reliance on AI-generated guidance"],
  },
];

// ── EverSoul framework layers ─────────────────────────────────────────────────
export const FRAMEWORK_LAYERS = [
  { n: 1, title: "Human intent", body: "The individual defines what may be preserved, who may access it, and under what conditions." },
  { n: 2, title: "Legal & fiduciary authority", body: "Trustees, executors, attorneys, or authorized representatives retain legal authority." },
  { n: 3, title: "EverSoul governance", body: "The platform enforces consent, permissions, identity fidelity, release conditions, auditing, and retirement rules." },
  { n: 4, title: "Personal AI", body: "The AI provides approved memories, values, voice, guidance, and personal context." },
  { n: 5, title: "Beneficiary experience", body: "Approved users interact with the personal AI according to defined permissions." },
];

export const AUTHORITY_HIERARCHY = [
  "Legally valid directive or trust instrument",
  "Trustee, executor, or authorized fiduciary",
  "EverSoul consent & governance rules",
  "Personal AI interaction",
  "Beneficiary experience",
];

export const FRAMEWORK_COMPONENTS = [
  "Identity fidelity", "Verified consent", "Permissioned access", "Inheritance conditions",
  "Human fiduciary authority", "Privacy governance", "Model integrity", "Audit trails",
  "Modification restrictions", "Retirement instructions", "Vendor continuity", "Conflict resolution",
];

// ── Family founder scenario options ───────────────────────────────────────────
export const FOUNDER_OPTIONS = [
  { id: "age", label: "Grant access at a particular age", summary: "provide access to designated beneficiaries only after a specified age" },
  { id: "graduation", label: "Release guidance after graduation", summary: "release selected guidance after documented educational milestones" },
  { id: "succession", label: "Provide business principles during leadership succession", summary: "surface business principles during defined leadership-succession events" },
  { id: "philanthropy", label: "Preserve philanthropic intentions", summary: "preserve and communicate philanthropic intentions" },
  { id: "confidential", label: "Restrict confidential family information", summary: "keep confidential family information permanently restricted" },
  { id: "no-retrain", label: "Prevent descendants from retraining the AI", summary: "prohibit beneficiaries from modifying or retraining the identity" },
  { id: "trustee-approval", label: "Require trustee approval for expanded access", summary: "require authorized-trustee approval before access is expanded" },
  { id: "suspend", label: "Suspend access during a dispute", summary: "allow an authorized trustee to suspend access during disputes" },
  { id: "record", label: "Record all interactions", summary: "record all interactions for audit" },
  { id: "retire", label: "Retire the identity permanently after a defined period", summary: "permanently retire the identity after a defined period" },
];

// ── Risk register ─────────────────────────────────────────────────────────────
const R = (name, cat, likelihood, severity, parties, control, capability, confidence) =>
  ({ name, cat, likelihood, severity, parties, control, capability, confidence });

export const RISK_CATEGORIES = ["Legal", "Fiduciary", "Technical", "Privacy", "Family", "Identity", "Commercial", "Operational"];

export const RISK_REGISTER = [
  R("Unauthorized identity creation", "Identity", "Medium", "High", "Individuals, families", "Verified consent & identity authentication before any capsule is created", "Verified consent architecture", "Medium"),
  R("Voice or likeness manipulation", "Identity", "High", "High", "Individuals, heirs", "Provenance tagging and identity-fidelity controls on every output", "Identity fidelity", "Medium"),
  R("Incorrect memories", "Technical", "Medium", "Medium", "Beneficiaries", "Source-linked memories and a correction workflow with human review", "Audit trails & correction workflow", "Medium"),
  R("Model drift", "Technical", "Medium", "Medium", "Beneficiaries", "Versioned capsule with drift monitoring and modification restrictions", "Model integrity", "Low"),
  R("Beneficiary manipulation", "Family", "Medium", "High", "Heirs, families", "Behavioral guardrails and fiduciary oversight of advisory outputs", "Human fiduciary authority", "Low"),
  R("Conflicting permissions", "Fiduciary", "Medium", "Medium", "Trustees, beneficiaries", "Deterministic permission resolution routed to a human on conflict", "Permissioned access", "Medium"),
  R("Unauthorized commercial use", "Commercial", "Medium", "High", "Estate, heirs", "Explicit commercial-use restrictions and takedown procedures", "Consent scopes", "Medium"),
  R("Privacy violations affecting living people", "Privacy", "Medium", "High", "Third parties", "Consent scoping and redaction of non-consenting individuals", "Privacy governance", "Low"),
  R("AI guidance mistaken for legal authority", "Legal", "High", "High", "Beneficiaries, fiduciaries", "Mandatory AI disclosure and clear subordination to legal instruments", "Authority hierarchy", "Medium"),
  R("Failure to retire the identity", "Operational", "Medium", "Medium", "Estate", "Enforced retirement instructions and scheduled decommission", "Retirement instructions", "Medium"),
  R("Vendor collapse or abandonment", "Operational", "Medium", "High", "Estate, institutions", "Data portability and vendor-continuity commitments", "Vendor continuity", "Low"),
  R("Loss of authentication records", "Technical", "Low", "High", "Institutions", "Append-only audit trail with independent verification", "Audit trails", "Medium"),
  R("Unauthorized retraining", "Technical", "Medium", "High", "Estate, heirs", "Modification restrictions enforced at the capsule level", "Modification restrictions", "Medium"),
  R("Conflicting trust instructions", "Fiduciary", "Medium", "Medium", "Trustees", "Escalation to authorized human fiduciary for resolution", "Conflict resolution", "Medium"),
  R("Identity theft", "Identity", "Medium", "High", "Individuals", "Strong identity authentication and access controls", "Identity authentication", "Medium"),
  R("Family disputes", "Family", "High", "Medium", "Families", "Suspension rights and audit records for dispute periods", "Human fiduciary authority", "Low"),
  R("Cross-border jurisdiction issues", "Legal", "High", "Medium", "Institutions", "Jurisdiction-aware governance configuration", "Governance configuration", "Low"),
  R("Platform abandonment of the individual", "Operational", "Low", "High", "Estate", "Retirement and export guarantees independent of platform", "Retirement instructions", "Low"),
];

// ── Readiness assessment ──────────────────────────────────────────────────────
export const READINESS_QUESTIONS = [
  "Does your estate planning process address personal AI?",
  "Is ownership of an AI identity defined?",
  "Is consent documented?",
  "Can different beneficiaries receive different access?",
  "Can a trustee suspend access?",
  "Can beneficiaries modify or retrain the AI?",
  "Are interactions recorded?",
  "Is the identity authenticated?",
  "Is there a process for correcting inaccurate memories?",
  "Is there a vendor continuity plan?",
  "Is there a permanent retirement process?",
  "Is human legal authority clearly superior to AI output?",
];
// Q6 is inverted (ability for beneficiaries to modify = worse). Handled in component.
export const READINESS_INVERTED = [5];

export const READINESS_LEVELS = [
  { min: 0, label: "Unaddressed", note: "Personal AI inheritance is not yet reflected in your governance." },
  { min: 4, label: "Recognized", note: "The issue is acknowledged but controls are largely absent." },
  { min: 7, label: "Partially Governed", note: "Some controls exist; key gaps remain across consent, access, and retirement." },
  { min: 10, label: "Institutionally Prepared", note: "A coherent governance posture across consent, fiduciary authority, and retirement." },
];

// ── Time horizon ──────────────────────────────────────────────────────────────
export const TIME_HORIZON = [
  { period: "Present – 2030", label: "AI normalization", classification: "published", items: ["Rapid enterprise adoption", "Voice cloning", "Persistent memory", "Personal agents", "Digital likeness", "Increased identity-fraud risk"] },
  { period: "2030 – 2035", label: "Personal AI persistence", classification: "modeled", items: ["Long-term personal agents", "Rich personal archives", "Interactive family history", "Early estate-planning language", "Consent & ownership disputes"] },
  { period: "2035 – 2045", label: "Formal governance adoption", classification: "modeled", items: ["Trust provisions for personal AI", "Fiduciary access control", "Authentication standards", "Institutional custody", "Audit requirements"] },
  { period: "2045 onward", label: "Potential normalization", classification: "modeled", items: ["Governed personal AI as an estate category", "Standard trust language", "Cross-platform portability", "Institutional oversight", "Defined retirement procedures"] },
];

// ── Institutional audiences ───────────────────────────────────────────────────
export const AUDIENCES = [
  { id: "private-bank", label: "Private Bank", lens: "Client retention, trust administration, family governance, multigenerational relationships, fiduciary exposure, and innovation leadership." },
  { id: "trust-company", label: "Trust Company", lens: "Access approval, fiduciary duty, audit-ability, vendor continuity, and defensible governance of interactive identity." },
  { id: "estate-attorney", label: "Estate Attorney", lens: "Testamentary authority, consent, ownership, beneficiary rights, disputes, enforceability, and model retirement." },
  { id: "family-office", label: "Family Office", lens: "Founder intent, succession, confidentiality, family education, governance, archives, and stewardship." },
  { id: "trustee", label: "Trustee", lens: "Access approval, suspension rights, audit-ability, conflicts, fiduciary responsibility, and vendor continuity." },
  { id: "insurer", label: "Insurer", lens: "Fraud, impersonation, voice cloning, unauthorized use, liability, and identity authentication." },
  { id: "technology", label: "Technology Company", lens: "Model integrity, authentication, data portability, long-term storage, interoperability, and system retirement." },
  { id: "family", label: "Family", lens: "Preserving a person's voice and values with dignity, clear consent, and human authority always in control." },
];

export const METHODOLOGY = {
  summary:
    "The EverSoul Legacy Intelligence Index combines published third-party research with EverSoul-constructed analytical indices. Published figures are attributed to their originating institutions and shown separately. EverSoul indices score AI capability and institutional governance readiness on a 0–100 scale and are presented as scenarios, not predictions.",
  scoring:
    "Capability and readiness indices aggregate weighted sub-factors (listed with each chart). Weights are equal-by-default unless a factor is judged materially more consequential for inheritance governance.",
  confidence:
    "Confidence reflects the strength of underlying evidence: High (published, directly sourced), Medium (calculated or triangulated), Low (modeled or early-signal).",
  limitations:
    "Forecasts are inherently uncertain. Modeled scenarios illustrate directional dynamics, not guaranteed outcomes. Third-party data remains subject to the methodologies and limitations of its original sources.",
};

export const CLASSIFICATION_META = {
  published: { label: "Published data", color: "#3A5A7A" },
  calculated: { label: "Calculated data", color: "#6E7B8B" },
  modeled: { label: "EverSoul modeled scenario", color: "#B08D57" },
  framework: { label: "Conceptual framework", color: "#1A1D24" },
};
